import React, { useState } from 'react';
import {
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { notificationService, NotificationApp } from '../services/notificationService';
import { authService } from '../services';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 16 },
  form: { padding: 24, maxWidth: 640 },
  title: { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 16 },
  field: { marginBottom: 16 },
  sectionLabel: { fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  radioRow: { marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 12, marginTop: -8, marginBottom: 16 },
  actions: { marginTop: 8 },
  alert: { marginTop: 16 },
}));

// A product must be chosen for every broadcast — no "all apps" blast, so each
// product gets its own targeted notification.
const APP_OPTIONS: { value: NotificationApp; label: string }[] = [
  { value: 'SUPER_PREP', label: 'Super Prep' },
  { value: 'SUPER_COACH', label: 'Super Coach' },
  { value: 'TEACHER_APP', label: 'For Teachers' },
];

const NotificationScreen: React.FC = () => {
  const classes = useStyles();

  // Only products this admin can access are selectable.
  const allowedAppOptions = APP_OPTIONS.filter((o) => authService.hasProduct(o.value));

  const [target, setTarget] = useState<'broadcast' | 'user'>('broadcast');
  const [app, setApp] = useState<NotificationApp>(allowedAppOptions[0]?.value || 'SUPER_PREP');
  const [mobile, setMobile] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSend = async () => {
    setError('');
    setSuccess('');

    if (!title.trim() || !body.trim()) {
      setError('Title and message are both required.');
      return;
    }
    if (target === 'user' && !mobile.trim()) {
      setError('Enter the recipient mobile number for a single-user send.');
      return;
    }
    if (url.trim() && !/^https?:\/\//i.test(url.trim())) {
      setError('The deep link must be a full URL starting with http:// or https://');
      return;
    }

    const appLabel = APP_OPTIONS.find((o) => o.value === app)?.label || app;
    const confirmMsg =
      target === 'user'
        ? `Send this notification to ${mobile.trim()} on ${appLabel}?`
        : `Send this notification to everyone on ${appLabel}?`;

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setSending(true);
    const res = await notificationService.sendPush({
      title: title.trim(),
      body: body.trim(),
      target,
      app,
      mobile: target === 'user' ? mobile.trim() : undefined,
      url: url.trim() || undefined,
    });
    setSending(false);

    if (!res.ok) {
      setError(res.error || 'Failed to send notification.');
      return;
    }

    const d = res.data || {};
    setSuccess(
      `Sent to ${d.recipients ?? 0} user(s) across ${d.devices ?? 0} device(s).`,
    );
    setTitle('');
    setBody('');
    setUrl('');
    if (target === 'user') setMobile('');
  };

  return (
    <div>
      <Typography variant="h5" className={classes.header}>
        Send Notification
      </Typography>

      <Grid container>
        <Grid item xs={12}>
          <Paper className={classes.form} elevation={1}>
            <Typography className={classes.title}>Compose a push notification</Typography>

            <Typography className={classes.sectionLabel}>Send to</Typography>
            <RadioGroup
              row
              className={classes.radioRow}
              value={target}
              onChange={(e) => setTarget(e.target.value as 'broadcast' | 'user')}
            >
              <FormControlLabel value="broadcast" control={<Radio color="primary" />} label="A whole app (broadcast)" />
              <FormControlLabel value="user" control={<Radio color="primary" />} label="A single user" />
            </RadioGroup>

            {/* Product applies to both broadcast and single-user sends — a
                single-user send only reaches that person's device for this product. */}
            <TextField
              className={classes.field}
              select
              fullWidth
              label="Product"
              variant="outlined"
              size="small"
              value={app}
              onChange={(e) => setApp(e.target.value as NotificationApp)}
            >
              {allowedAppOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>

            {target === 'user' && (
              <TextField
                className={classes.field}
                fullWidth
                label="Recipient mobile number"
                variant="outlined"
                size="small"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            )}

            <TextField
              className={classes.field}
              fullWidth
              label="Title"
              variant="outlined"
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 80 }}
            />

            <TextField
              className={classes.field}
              fullWidth
              label="Message"
              variant="outlined"
              size="small"
              multiline
              minRows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              inputProps={{ maxLength: 240 }}
            />

            <TextField
              className={classes.field}
              fullWidth
              label="Deep link URL (optional)"
              placeholder="https://xarothi.in/..."
              variant="outlined"
              size="small"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <div className={classes.hint}>
              If set, tapping the notification opens this link.
            </div>

            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : 'Send notification'}
              </Button>
            </div>

            {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
            {success && <Alert severity="success" className={classes.alert}>{success}</Alert>}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default NotificationScreen;
